import { useMemo } from 'react';

import { LayerPanel, TerrainNotice } from '../maps/LayerPanel.js';
import { MapNotice } from '../maps/MapNotice.js';
import { OffMapStrip } from '../maps/OffMapStrip.js';
import { RegionMap, type MapContextKind, type MapModifierClick } from '../maps/RegionMap.js';
import type { ScenarioFacadeState } from '../maps/useScenarioFacade.js';
import {
  applyPick,
  naturalSlotFor,
  pickRowsFor,
  targetForSubject,
  type PickRow,
} from '../model/builder.js';
import { armedCount, filterRoster } from '../model/filter.js';
import { MAP_LAYER_LABELS } from '../model/map-layers.js';
import { paramSpecOf } from '../model/missions.js';
import { toDrafts } from '../model/queue.js';
import { rosterOf } from '../model/roster.js';
import { offMapNoticeOf, resolveSelection } from '../model/selection.js';
import { useConsoleStore } from '../state/store.js';
import type { CellViewWire } from '@badger-sentinel/protocol';

/**
 * The theatre map pane — the design's `taskableView` for `REGION MAP` (343–372).
 *
 * The canvas is `vendor/maps/region-map.js` behind `maps/RegionMap.tsx`; this
 * component is what stands between it and the console — the layer control, the
 * four reasons there might be no map, and the translation of the seven gestures
 * the canvas reports into the store writes the rest of the shell already makes.
 *
 * ## Every gesture ends where the same gesture from the rail ends
 *
 * A marker click and a roster row click both call `select`. A right-click on a
 * marker and a right-click on a roster row both open the `unit` menu on the same
 * subject. ⇧-click fills the slot `naturalSlotFor` picks, which is the function
 * the context menu already uses to decide which of its two builder starters to
 * offer. A drag from an asset onto a contact opens the mission picker on a
 * builder that already knows both ends. None of that is re-decided here: this
 * file routes, and `model/` decides, so the map and the rails cannot drift apart
 * about what a click means.
 *
 * ## The filter dims, it never hides
 *
 * `api.setDim` takes the ids the roster filter matched. Markers that do not match
 * drop to a quarter opacity and **keep their slot** — the count badges then
 * report `hit/total` rather than rounding the filter away, which is the
 * renderer's own rule and the reason a filtered map cannot hide a force. Nothing
 * here ever removes a unit from the model to filter it.
 */

/**
 * Lower than the 420 the other four modules share, and deliberately.
 *
 * Those four scroll: a list or a report that runs past the fold is read by
 * scrolling, and 420 keeps them from collapsing into a slot. This one does not.
 * The map is a canvas that *scales*, and its own chrome — LEGEND bottom-left, the
 * zoom column and scale readout bottom-right — is pinned to the canvas's bottom
 * edge. At the committed 1280 × 720 floor the pane column offers 395 px, so a 420
 * floor bought 25 px of extra map by pushing LEGEND 15 px below the fold and
 * hiding the scale readout outright; on CREWCOM, whose read-only banner takes
 * another 24, LEGEND disappeared. That is the failure the floor exists to prevent,
 * caused by the floor.
 *
 * 260 is a map that is still a map. It clears 720 with the read-only banner and
 * the GM strip both up, so on every viewport this console supports the theatre
 * now takes the remainder and nothing on it is elided.
 */
export const THEATRE_MIN_HEIGHT = 260;

export interface TheatreViewProps {
  readonly view: CellViewWire;
  readonly facade: ScenarioFacadeState;
  /** The `unit` / `zone` / `canvas` menus, which the shell owns and routes. */
  readonly onContextMenu: (
    subject: 'unit' | 'zone' | 'canvas',
    id: string,
    x: number,
    y: number,
  ) => void;
}

const CONTEXT_SUBJECT: Readonly<Record<MapContextKind, 'unit' | 'zone' | 'canvas'>> = Object.freeze(
  { unit: 'unit', region: 'zone', empty: 'canvas' },
);

export function TheatreView(props: TheatreViewProps): JSX.Element {
  const view = props.view;

  const selection = useConsoleStore((state) => state.selection);
  const select = useConsoleStore((state) => state.select);
  const layers = useConsoleStore((state) => state.mapLayers);
  const toggleMapLayer = useConsoleStore((state) => state.toggleMapLayer);
  const panelOpen = useConsoleStore((state) => state.mapLayerPanelOpen);
  const setPanelOpen = useConsoleStore((state) => state.setMapLayerPanelOpen);
  const terrain = useConsoleStore((state) => state.mapTerrain);
  const terrainAvailable = useConsoleStore((state) => state.mapTerrainAvailable);
  const setMapTerrain = useConsoleStore((state) => state.setMapTerrain);
  const noteTerrainUnavailable = useConsoleStore((state) => state.noteTerrainUnavailable);
  const hoverId = useConsoleStore((state) => state.hoverId);
  const setHover = useConsoleStore((state) => state.setHover);
  const command = useConsoleStore((state) => state.mapCommand);
  const clearMapCommand = useConsoleStore((state) => state.clearMapCommand);

  const search = useConsoleStore((state) => state.search);
  const facets = useConsoleStore((state) => state.facets);
  const queue = useConsoleStore((state) => state.queue);
  const builder = useConsoleStore((state) => state.builder);
  const armedSlot = useConsoleStore((state) => state.armedSlot);
  const setBuilder = useConsoleStore((state) => state.setBuilder);
  const armSlot = useConsoleStore((state) => state.armSlot);
  const fillSlot = useConsoleStore((state) => state.fillSlot);
  const fillMission = useConsoleStore((state) => state.fillMission);
  const fillTarget = useConsoleStore((state) => state.fillTarget);
  const fillParam = useConsoleStore((state) => state.fillParam);
  const toggleBatch = useConsoleStore((state) => state.toggleBatch);
  const showToast = useConsoleStore((state) => state.showToast);

  /**
   * What the roster filter matched, or `null` for "no filter running".
   *
   * The same three inputs the left rail filters on, through the same two
   * functions — a second predicate here would be a map that disagrees with the
   * list beside it about which units are interesting.
   */
  const dim = useMemo<readonly string[] | null>(() => {
    if (search === '' && armedCount(facets) === 0) return null;
    const roster = rosterOf(view, toDrafts(queue));
    return filterRoster(roster, search, facets).flatMap((group) => group.rows.map((row) => row.id));
  }, [view, queue, search, facets]);

  /**
   * The open slot's candidates, so a click on the thing itself fills the slot.
   *
   * Only the two slots a map can answer: a mission and a salvo size are not on
   * the canvas. The rows are kept, not just their ids, because taking one is
   * `applyPick` — the same function the picker and the number-row shortcut use.
   */
  const pickRows = useMemo<readonly PickRow[]>(
    () =>
      armedSlot === 'asset' || armedSlot === 'target' ? pickRowsFor(view, builder, armedSlot) : [],
    [view, builder, armedSlot],
  );
  const pick = useMemo(
    () =>
      armedSlot !== null && pickRows.length > 0
        ? { slot: armedSlot, ids: pickRows.map((row) => row.id) }
        : null,
    [armedSlot, pickRows],
  );

  const takeRow = (row: PickRow): void => {
    applyPick(row, {
      asset: (id) => fillSlot('asset', id),
      mission: fillMission,
      target: fillTarget,
      param: (value) => {
        const spec = builder.mission === null ? null : paramSpecOf(builder.mission);
        if (spec !== null) fillParam(spec.key, value);
      },
    });
  };

  /** ⇧ fills the natural slot, ⌥ forces the target slot, ⌘ collects into the batch. */
  const onModClick = (detail: MapModifierClick): void => {
    if (detail.meta) {
      toggleBatch(detail.id);
      return;
    }
    const slot = detail.alt ? 'target' : detail.shift ? naturalSlotFor(view, detail.id) : null;
    if (slot === null) return;
    if (slot === 'asset') {
      fillSlot('asset', detail.id);
      showToast('ASSET ← ' + detail.id);
      return;
    }
    const target = targetForSubject(view, detail.id);
    if (target === null) {
      showToast(detail.id + ' CANNOT BE A TARGET', 'warn');
      return;
    }
    fillTarget(target);
    showToast('TARGET ← ' + detail.id);
  };

  /**
   * A drag from one marker to another states both ends of an order, so all that
   * is left to ask is the mission — and the picker opens armed on it.
   *
   * The drag is directional on the canvas and is not trusted to be: an operator
   * who drags from the threat to their own launcher meant the same order, and
   * the design flips it rather than refusing it.
   */
  const onEngage = (actorId: string, targetId: string): void => {
    const actorIsOwn = view.friendly.some((asset) => asset.id === actorId);
    const targetIsOwn = view.friendly.some((asset) => asset.id === targetId);
    const [from, to] = actorIsOwn || !targetIsOwn ? [actorId, targetId] : [targetId, actorId];
    if (!view.friendly.some((asset) => asset.id === from)) {
      showToast('DRAG MUST START ON A FRIENDLY ASSET', 'warn');
      return;
    }
    const target = targetForSubject(view, to);
    if (target === null) {
      showToast(to + ' CANNOT BE A TARGET', 'warn');
      return;
    }
    setBuilder({ asset: from, mission: null, target, params: {} });
    armSlot('mission');
    showToast(from + ' → ' + to + ' · CHOOSE A MISSION');
  };

  /**
   * The one thing the canvas cannot answer for, answered beside it.
   *
   * Roughly half of every seat's order of battle is in orbit, and the theatre map
   * draws ground. `offMapNoticeOf` returns null whenever the canvas *can* speak,
   * so this strip is present exactly when the map would otherwise have gone
   * silent under a click that did something everywhere else on screen.
   */
  const offMap = useMemo(
    () => offMapNoticeOf(resolveSelection(view, selection), 'THEATRE'),
    [view, selection],
  );

  const facade = props.facade;
  const body =
    facade.status === 'ready' ? (
      <>
        {/*
          Before the map in the DOM, not after. Both float over the canvas — LAYERS
          at the top-right, the renderer's own zoom column at the bottom-right — and
          the panel is absolutely positioned above them on `--z-map-panel`, so order
          here costs no layout. It buys the tab ring: LAYERS used to come *after* the
          five vendored buttons that sit below it, so keyboard order ran up the screen.
        */}
        <LayerPanel
          labels={MAP_LAYER_LABELS}
          layers={layers}
          open={panelOpen}
          notice={terrainAvailable ? null : <TerrainNotice />}
          onToggleOpen={() => setPanelOpen(!panelOpen)}
          onToggle={toggleMapLayer}
        />
        <RegionMap
          model={facade.model}
          layers={layers}
          selectedUnit={
            selection === null || selection.kind === 'REGION' || selection.kind === 'MW_TRACK'
              ? null
              : selection.id
          }
          dim={dim}
          pick={pick}
          hover={hoverId}
          command={command}
          terrain={terrain}
          onSelect={(id) => select(id === null ? null : subjectOf(view, id))}
          onRegion={(id) => {
            if (id !== null) select({ kind: 'REGION', id });
          }}
          onPick={(slot, id) => {
            const row = pickRows.find(
              (candidate) => candidate.slot === slot && candidate.id === id,
            );
            if (row !== undefined) takeRow(row);
          }}
          onContext={(kind, id, x, y) => props.onContextMenu(CONTEXT_SUBJECT[kind], id, x, y)}
          onHover={setHover}
          onModClick={onModClick}
          onEngage={onEngage}
          onCommandApplied={clearMapCommand}
          onTerrainUnavailable={noteTerrainUnavailable}
          onTerrainChanged={setMapTerrain}
        />
        {offMap !== null && <OffMapStrip notice={offMap} testId="theatre-off-map" />}
      </>
    ) : (
      notice(facade)
    );

  return (
    <div
      data-testid="theatre-view"
      style={{
        position: 'relative',
        // `1 1 auto` against a floor low enough that it does not engage on any
        // viewport this console supports — see THEATRE_MIN_HEIGHT. The map takes
        // the remainder of the pane column; only below the supported range does
        // the floor bite and the column scroll.
        flex: '1 1 auto',
        minHeight: THEATRE_MIN_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--canvas)',
      }}
    >
      {body}
    </div>
  );
}

/** A map click names an id; the view says whether that is an asset or a contact. */
function subjectOf(
  view: CellViewWire,
  id: string,
): { readonly kind: 'ASSET' | 'CONTACT'; readonly id: string } | null {
  if (view.contacts.some((contact) => contact.id === id)) return { kind: 'CONTACT', id };
  if (view.friendly.some((asset) => asset.id === id)) return { kind: 'ASSET', id };
  return null;
}

function notice(facade: ScenarioFacadeState): JSX.Element | null {
  switch (facade.status) {
    case 'loading':
      // Transient, and one same-origin request: a flash of text is worse than a
      // beat of the canvas colour.
      return null;
    case 'absent':
      return <MapNotice testId="theatre-map-absent" title="NO MAP PACK FOR THIS SCENARIO" />;
    case 'invalid':
      return (
        <MapNotice
          testId="theatre-map-invalid"
          title="MAP PACK UNREADABLE"
          detail={facade.reason}
        />
      );
    case 'incomplete':
      return (
        <MapNotice
          testId="theatre-map-incomplete"
          title="MAP PACK INCOMPLETE"
          detail={'NO GEOMETRY FOR ' + facade.missing.join(' · ')}
        />
      );
    default:
      return null;
  }
}
